<script lang="ts" setup>
import { v4 as uuid } from 'uuid'
import type { RankingPageConfig } from '../configs/types'
import { useRanking } from '../composables/useRanking'
import { brand } from '@/config/brands'
import { prefixCDN } from '@/utils/cdn'

interface Props {
  columnId: string
  config: RankingPageConfig
}

const props = defineProps<Props>()

const {
  title,
  books,
  collections,
  fetching,
  ending,
  infiniteRef,
} = useRanking(props.columnId, props.config)

// 徽章与首页榜单模块同款书签形（Top1-3 专属，4 名及以后同款灰色）
function getMedalIcon(index: number) {
  const top = index <= 2 ? index + 1 : 4
  return prefixCDN(`/images/${brand}/home/rank-top${top}.png`)
}
</script>

<template>
  <div class="page h-100dvh overflow-scroll">
    <BasicNavbar class="top-2 color-n-text-2" />

    <div class="title h-60px">
      {{ title || '' }}
    </div>

    <div ref="infiniteRef" class="container rounded">
      <div v-for="(book, index) in collections" :key="index">
        <router-link
          :to="{ name: 'book', params: { contentId: book.id } }" replace
          class="flex items-center px-3 py-2"
        >
          <div class="book-cover-wrapper w-66px h-88px shrink-0 relative">
            <BizBookCover
              :id="uuid()"
              :src="book.contentCoverUrl || ''"
              :extra="{
                module_name: books.showName,
                module_id: books.id,
                module_type: books.columnType,
                author: book.authorInfo.author,
                ...book,
              }"
              class="w-full h-full border-rd-8px overflow-hidden"
            />
            <div class="medal">
              <img :src="getMedalIcon(index)">
              <div class="medal-num">
                {{ index + 1 }}
              </div>
            </div>
          </div>

          <div class="flex-1 overflow-hidden ml-3">
            <div class="font-500 truncate text-n-text-1">
              {{ book.contentName }}
            </div>
            <BizShowTags v-if="book.tagList && book.tagList.length" :tags="book.tagList" class="mt-2" />
            <div class="text-n-text-3 text-12px line-clamp-2 mt-2">
              {{ book.introduce }}
            </div>
          </div>
        </router-link>
      </div>

      <div v-if="ending" class="py-15px text-13px text-n-text-3 text-center">
        {{ config.texts.noMore }}
      </div>
      <div v-else-if="fetching" class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-3">
        <div class="animate-spin">
          <i-icon-park-outline-loading />
        </div>
        <p>{{ config.texts.loading }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 深色主题：弃用旧亮色奖杯背景图（WGSQ-477） */
.page {
  padding: 0 10px;
}

.title {
  font-weight: 500;
  font-size: 18px;
  color: #ffffff;
  line-height: 16px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.book-cover-wrapper {
  position: relative;
}

/* 书签形徽章：贴封面左上角，数字略上移视觉居中（与首页 ModuleRankingBig 一致） */
.medal {
  position: absolute;
  top: 0;
  left: 3px;
  z-index: 10;
  width: 17px;
  height: 19px;
}

.medal img {
  width: 100%;
  height: 100%;
}

.medal-num {
  position: absolute;
  inset: 0;
  padding-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
}

.container {
  overflow-y: scroll;
  height: calc(100dvh - 70px);
  padding-top: 10px;
}
</style>
